import { AfterViewInit, Component, Inject, Input } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { SKUValidator } from 'src/app/utils/sku-validator';
import { APIOrderline } from '../../APIVerkooporders';
import { APIOrderlineToWrite } from '../../APIVerkoopordersToWrite';
import { OrderlineForm } from '../../form-controls';

@Component({
  selector: 'app-new-salesorder-orderlines',
  templateUrl: './new-salesorder-orderlines.component.html',
  styleUrls: ['./new-salesorder-orderlines.component.scss'],
})
export class NewSalesorderOrderlinesComponent implements AfterViewInit {
  static requiredValidator = Validators.required.bind(null);

  // Customer-context for the ATP check (threaded down to app-atp-info).
  @Input() customerNumber?: string;
  @Input() businessCustomer: boolean = false;
  @Input() orderlines!: FormArray<FormGroup<OrderlineForm>>;

  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private fb: FormBuilder
  ) {}

  get hasSelectedOrderlines(): boolean {
    return this.orderlines.value.some(o => o.checked);
  }

  get selectedOrderlinesAreValid(): boolean {
    const hasInvalid = this.orderlines.controls.some(formGroup => formGroup.controls.checked.value && !formGroup.valid);
    return !hasInvalid;
  }

  get valid(): boolean {
    return this.hasSelectedOrderlines && this.selectedOrderlinesAreValid;
  }

  get allOrderlinesSelected(): boolean {
    return this.orderlines.length > 0 && this.orderlines.controls.every(fg => fg.controls.checked.value);
  }

  static createOrderlinesFormArray(fb: FormBuilder, orderlines: APIOrderline[]): FormArray<FormGroup<OrderlineForm>> {
    const groups = orderlines.map(ol => this.createOrderlineGroup(fb, ol));
    return fb.array(groups);
  }

  static createOrderlineGroup(fb: FormBuilder, orderline: APIOrderline): FormGroup<OrderlineForm> {
    const sku = orderline.item.sku;
    const name = orderline.item.name;
    const qty = orderline.quantity;
    const ref = orderline.orderlineRef ?? '';
    const validationMessage = orderline.validationMessage ?? '';
    const warningMessage = orderline.warningMessage ?? '';

    return fb.group<OrderlineForm>({
      rowNumber: new FormControl<number>(orderline.rowNumber, { nonNullable: true }),
      checked: new FormControl<boolean>(!orderline.validationMessage, { nonNullable: true }),
      sku: new FormControl<string>(sku, { nonNullable: true, validators: [this.requiredValidator, SKUValidator] }),
      name: new FormControl<string>(name, { nonNullable: true }),
      quantity: new FormControl<number>(qty, {
        nonNullable: true,
        validators: [this.requiredValidator, Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?|10000)$/)],
      }),
      orderlineRef: new FormControl<string>(ref, { nonNullable: true }),
      validationMessage: new FormControl<string>(validationMessage, { nonNullable: true }),
      warningMessage: new FormControl<string>(warningMessage, { nonNullable: true }),
    });
  }

  ngAfterViewInit(): void {
    if (this.orderlines.length === 0) {
      this.addNewOrderline();
    }
  }

  addNewOrderline(): void {
    const existingRows = this.orderlines.controls.map(formGroup => formGroup.controls.rowNumber.value);
    const nextRow = existingRows.length ? Math.max(...existingRows) + 1 : 1;
    const newOrder: APIOrderline = {
      item: { sku: '' },
      quantity: 0,
      orderlineRef: '',
      rowNumber: nextRow,
      validationMessage: '',
    } as APIOrderline;

    const fg = NewSalesorderOrderlinesComponent.createOrderlineGroup(this.fb, newOrder);
    fg.markAllAsTouched();
    this.orderlines.push(fg);
  }

  getOrderlines(): APIOrderlineToWrite[] {
    return this.orderlines
      .getRawValue()
      .filter(l => l.checked)
      .map(l => ({
        rowNumber: l.rowNumber,
        item: { sku: l.sku },
        quantity: l.quantity,
        orderlineRef: l.orderlineRef,
      }));
  }

  selectAllOrderlines(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.orderlines.controls.forEach(fg => {
      fg.controls.checked.setValue(checked);
    });
  }
}
